import * as React from 'react';
import {
    TreeList, orderBy, filterBy, mapTree, extendDataItem, treeToFlat,
    TreeListTextFilter, createDataTree
} from '@progress/kendo-react-treelist';
import { Code } from '../../models/Code';
import { FilterDescriptor } from '@progress/kendo-data-query';
import ProductService from '../../services/ProductService';
import { Product } from '../../models/Product';

const subItemsField = 'subItems';
const expandField = 'expanded';
const selectedField = 'selected';

export type ProductFamilyTreeSelectedItemInfo = {
    product: Product;
    fullName: string;
};

type ProductFamilyTreeProps = {
    onSelectedChanged?: (info: ProductFamilyTreeSelectedItemInfo) => void;
}

type ProductFamilyTreeState = {
    items: TreeItem[];
    filteredItems: TreeItem[];
    itemsSort: any[];
    sortOnRecalc: boolean;
    filter: FilterDescriptor[];
    prevFilter: FilterDescriptor[];
    flatItems: TreeItem[];
    expanded: string[];
    selected: string;
    selectedItem: TreeItem;
    selectedFullName: string;
    codes: Code[];
    selectedCodes: Code[];

};


class ProductFamilyTree extends React.Component<ProductFamilyTreeProps, ProductFamilyTreeState> {

    constructor(props) {

        super(props);

        this.state = {
            items: [],
            filteredItems: [],
            itemsSort: [{
                field: "Name", dir: "asc"
            }],
            sortOnRecalc: true,
            flatItems: [],
            filter: [],
            prevFilter: [],
            expanded: [],
            selected: undefined,
            selectedItem: undefined,
            selectedFullName: '',
            codes: [],
            selectedCodes: []
        };
    }

    componentDidMount() {
        this.loadAll();
    }

    loadAll = async () => {

        const productsResult = await ProductService.list();

        if (!productsResult.hasErrors) {

            const flatItems = productsResult.value.map(x => TreeItem.fromProduct(x));

            const productTreeItems = createDataTree(flatItems, (x: TreeItem) => x.Id, (x: TreeItem) => x.ParentId, subItemsField);

            this.setState({
                items: productTreeItems,
                sortOnRecalc: true
            }, this.recalcFilteredItems);

        } else {
            console.error('tree data not loaded');
        }
    }

    handleRowClick = (e) => {

        this.setState({
            selected: (e.dataItem as TreeItem).Id,
            selectedItem: (e.dataItem as TreeItem),
            selectedFullName: this.buildFullName((e.dataItem as TreeItem))
        }, () => {
                this.fireOnSelectedChangeEvent();
            this.recalcFilteredItems();
        });
    }

    onExpandChange = (e) => {

        this.setState({
            expanded: e.value ?
                this.state.expanded.filter(id => id !== (e.dataItem as TreeItem).Id) :
                [...this.state.expanded, (e.dataItem as TreeItem).Id]
        }, () => { this.recalcFilteredItems() });
    }

    handleFilterChange = (event) => {
        
        this.setState({
            filter: event.filter
        }, () => { this.recalcFilteredItems(); })
    }

    fireOnSelectedChangeEvent = () => {
        if (!this.props.onSelectedChanged)
            return;

        const product = this.state.selectedItem ? this.state.selectedItem.dataItem as Product : undefined;
        const fullName = this.buildFullName(this.state.selectedItem);

        this.props.onSelectedChanged({ fullName, product });
    }

    recalcFilteredItems = () => {

        let sortOnRecalc = this.state.sortOnRecalc;
        let items = this.state.items;
        if (sortOnRecalc) {

            items = orderBy(items, this.state.itemsSort, subItemsField);
            sortOnRecalc = false;
        }

        var data = mapTree(items, subItemsField, (item: TreeItem) =>
            extendDataItem(item, subItemsField, {
                [expandField]: this.state.expanded.indexOf(item.Id) > -1,
                [selectedField]: this.state.selected === item.Id
            }));

        let filteredItems = filterBy(data, this.state.filter, subItemsField);

        let hasFilter = this.state.filter.length > 0 && this.state.filter[0].value.length > 1;

        let doExpand = hasFilter && this.state.prevFilter !== this.state.filter;

        let infoData = doExpand
            ? this.expandAsDeepAsPosible(filteredItems, [], filteredItems.length > 0 ? filteredItems[0] : undefined)
            : {
                expandedIds: this.state.expanded,
                selectedItem: this.state.selectedItem
            };

        filteredItems = mapTree(filteredItems, subItemsField, (item: TreeItem) =>
            extendDataItem(item, subItemsField, {
                [expandField]: infoData.expandedIds.indexOf(item.Id) > -1,
                [selectedField]: this.state.selected === item.Id
            }));

        this.setState({
            filteredItems: filteredItems,
            expanded: infoData.expandedIds,
            prevFilter: this.state.filter,
            items,
            sortOnRecalc            
        });
    }

    expandAsDeepAsPosible = (items: TreeItem[], expandedIds: string[], selectedItem: TreeItem) => {

        expandedIds = expandedIds || [];

        if (items && items.length > 0) {

            if (expandedIds.indexOf(items[0].Id) < 0)
                expandedIds.push(items[0].Id);

            return this.expandAsDeepAsPosible(items[0].subItems, expandedIds, items[0]);
        }

        return { expandedIds, selectedItem };
    }

    buildFullName = (item: TreeItem) => {

        console.log('buildFullName', item);

        var result = [];
        var currentItem = item;
        let fullName = '';


        if (item) {

            do {
                result.push(currentItem);

                fullName = currentItem.Name + (fullName.length > 0 ? ' / ' : '') + fullName;

                currentItem = currentItem.parentDataItem;


            } while (currentItem);
        }

        return fullName;
    }

    render() {

        return (<TreeList
            className="table-body-scroll"
            data={this.state.filteredItems}
            columns={[{ field: 'Name', title: 'Product', filter: TreeListTextFilter, expandable: true }]}
            onRowClick={this.handleRowClick}
            selectedField={selectedField}
            expandField={expandField}
            filter={this.state.filter}
            sort={this.state.itemsSort}
            subItemsField={subItemsField}
            onExpandChange={this.onExpandChange}
            onFilterChange={this.handleFilterChange}
            
        />);
    }
}

class TreeItem {
    Id: string;
    Name: string;
    ParentId?: string;
    type: 'division' | 'product';
    subItems: TreeItem[];
    dataItem: any;
    parentDataItem: TreeItem;
    expanded: boolean;

    public static fromProduct(item: Product) {

        const result: TreeItem = {
            dataItem: item,
            type: 'product',
            subItems: undefined,
            Name: item.Description,
            Id: item.ProductId,
            ParentId: item.ParentProductId,
            parentDataItem: undefined,
            expanded: false
        };

        return result;
    }

}

export default ProductFamilyTree;